/**
 * brokerHiddenFields — columns an EXTERNAL brokerage must not see at all.
 *
 * Stricter than the RESERVED columns handled by `applyFieldEditability`. A
 * reserved column (`land_system_lease_id`, `wegid`, `lease_integrated_date`,
 * `well_integrated_date`, the deal integration flags) stays VISIBLE and merely
 * renders read-only — the brokerage may read the value, just not write it. The
 * columns here are WIG's own numbers, so a BD.BrokerAgent must not see them
 * either: they drop out of the metadata entirely, which takes them off the
 * grid, out of the detail form, out of the drawer, and out of the bulk-edit
 * field list in one move.
 *
 * | Table         | Column              | Why                                    |
 * |---------------|---------------------|----------------------------------------|
 * | `deals`       | `promote`           | WIG's promote economics                |
 * | `deals`       | `promote_due_date`  | WIG's promote economics                |
 * | `lease_deeds` | `lease_cost`        | the PSA allocated value — WIG's        |
 * |               |                     | allocation, not a brokerage term       |
 * | `wells`       | `allocated_value`   | WIG's allocation of purchase price     |
 * |               |                     | across wells                           |
 *
 * `lease_cost` is the one with a paper trail worth knowing about. The field
 * inventory (`br_files/BrokerTrackerBackEndChanges.xlsx`) marks it
 * `Broker Editable? Y`, and de-hub's `permissions.py` leaves it unreserved on
 * the strength of that. But the inventory carries only "Broker Editable?" and
 * "WEG User Editable?" columns — it never asked a visibility question — and its
 * own note on the row reads "PSA allocated value", i.e. WIG's allocation out of
 * the purchase and sale agreement rather than the brokerage's submitted $/acre.
 * Hence hidden here despite the `Y`.
 *
 * UI-ONLY, and deliberately so: `GET /deals`, `GET /lease_deeds` and
 * `GET /wells` all still return these columns, so this is presentation, not a
 * data boundary. The server-side half — omitting them from an external caller's
 * payloads, from the upload template export, and from the upload preview diff —
 * is specified in `uploadspec/BDA_Update_Loader_Build_Spec.md` §3.1 and §7.
 * Until that lands, treat this module as the single place the field list is
 * written down, so the server work has one list to copy rather than four.
 */

/** Table name -> the fields an external brokerage must not see. */
export const BROKER_HIDDEN_FIELDS = {
  deals: ["promote", "promote_due_date"],
  lease_deeds: ["lease_cost"],
  wells: ["allocated_value"],
};

/**
 * isExternalBrokerAgent — BD.BrokerAgent WITHOUT any cross-brokerage
 * visibility.
 *
 * The `deal.viewAll` half is what keeps an internal user who also happens to
 * hold BrokerAgent on the internal view, mirroring de-hub's
 * `resolve_broker_scope` (a caller is scoped only when BrokerAgent is their
 * ONLY broker-deals role). Centralized here because the same expression was
 * written out by hand in the deals list and in LeaseDeedsTab, and the two must
 * never drift.
 *
 * @param {{ hasRole: (r: string) => boolean, can: (a: string) => boolean }} perms
 *   the `usePermissions()` result
 * @returns {boolean}
 */
export const isExternalBrokerAgent = (perms) => {
  if (!perms) return false;
  const { hasRole, can } = perms;
  if (typeof hasRole !== "function" || typeof can !== "function") return false;
  return hasRole("BD.BrokerAgent") && !can("deal.viewAll");
};

/**
 * brokerHiddenFieldSet — the hidden fields for one table, lowercased.
 *
 * Lowercased because the two metadata endpoints disagree on casing: the v2
 * `/metadata` rows this app mostly reads carry snake_case `field` values, while
 * the v1 `/broker_deals/metadata` feeding `useBrokerDeals` follows the legacy
 * MSSQL uppercase contract. Matching case-insensitively means one list covers
 * both instead of a hidden field slipping through on whichever endpoint the
 * caller happened to use.
 *
 * @param {string} tableName
 * @returns {Set<string>}
 */
export const brokerHiddenFieldSet = (tableName) =>
  new Set((BROKER_HIDDEN_FIELDS[tableName] || []).map((f) => f.toLowerCase()));

/**
 * filterBrokerHiddenFields — drop the hidden columns from a metadata array
 * when the caller is an external brokerage.
 *
 * A no-op for internal callers, so it is safe to wrap every metadata consumer
 * unconditionally.
 *
 * @param {Array<object>} metadata — UIMetadata rows for one table
 * @param {string} tableName — key into `BROKER_HIDDEN_FIELDS`
 * @param {boolean} isBrokerAgent — see `isExternalBrokerAgent`
 * @returns {Array<object>} the rows the caller may see
 */
export const filterBrokerHiddenFields = (
  metadata,
  tableName,
  isBrokerAgent
) => {
  const rows = Array.isArray(metadata) ? metadata : [];
  if (!isBrokerAgent) return rows;
  const hidden = brokerHiddenFieldSet(tableName);
  if (hidden.size === 0) return rows;
  return rows.filter(
    (m) => !hidden.has(String(m?.field ?? "").toLowerCase())
  );
};

/**
 * filterBrokerHiddenChildMetadata — `filterBrokerHiddenFields` over a
 * `{ [tableName]: rows }` map, such as the deals list's child-table metadata
 * that feeds the filter picker (`lease_deeds.lease_cost`,
 * `wells.allocated_value`). The keys are the filter prefixes, which are also
 * the table names.
 *
 * de-hub already omits these rows from `/metadata` for an external caller;
 * this is the client-side backstop, so a stale or older server cannot put a
 * hidden column back in the picker.
 *
 * @param {Record<string, Array<object>>} childMetadata
 * @param {boolean} isBrokerAgent
 * @returns {Record<string, Array<object>>}
 */
export const filterBrokerHiddenChildMetadata = (childMetadata, isBrokerAgent) => {
  const src = childMetadata && typeof childMetadata === "object" ? childMetadata : {};
  if (!isBrokerAgent) return src;
  const out = {};
  for (const [table, rows] of Object.entries(src)) {
    out[table] = filterBrokerHiddenFields(rows, table, true);
  }
  return out;
};

/**
 * isBrokerHiddenFilterField — does a filter / sort field name point at a
 * column an external brokerage must not see?
 *
 * Resolves names the way the server's filter compiler does: `prefix.column`
 * names `column` on the child table `prefix`; a bare name is a `deals`
 * column. A bare child prefix (`wells`, the has-any / has-none entry) names
 * no column and is never hidden. A leading `-` (descending sort) is ignored.
 *
 * @param {string} name
 * @returns {boolean}
 */
export const isBrokerHiddenFilterField = (name) => {
  const field = String(name ?? "").replace(/^-/, "").toLowerCase();
  if (!field) return false;
  const dot = field.indexOf(".");
  if (dot === -1) return brokerHiddenFieldSet("deals").has(field);
  const table = field.slice(0, dot);
  const column = field.slice(dot + 1);
  return brokerHiddenFieldSet(table).has(column);
};

/**
 * withoutBrokerHiddenRules — the filter tree with every rule on a hidden
 * column removed (nested groups included; a group left empty is dropped).
 *
 * The picker never offers these columns to a broker, but a tree can still
 * carry one from somewhere else — the tab's remembered filter (stickyQuery),
 * a pasted or older saved query. de-hub answers such a search with 422
 * "Invalid field name", which would leave the grid on an error a broker has
 * no way to clear. Dropping the rule keeps the rest of their filter working.
 *
 * A no-op (same object back) for internal callers and for a tree with
 * nothing to drop.
 *
 * @param {object|null|undefined} query  RuleFilter tree ({combinator, rules})
 * @param {boolean} isBrokerAgent
 * @returns {object|null|undefined}
 */
export const withoutBrokerHiddenRules = (query, isBrokerAgent) => {
  if (!isBrokerAgent || !query || !Array.isArray(query.rules)) return query;
  let changed = false;
  const rules = [];
  for (const rule of query.rules) {
    if (rule && Array.isArray(rule.rules)) {
      const inner = withoutBrokerHiddenRules(rule, true);
      if (inner !== rule) changed = true;
      if (inner.rules.length) rules.push(inner);
      else changed = true;
    } else if (rule && isBrokerHiddenFilterField(rule.field)) {
      changed = true;
    } else {
      rules.push(rule);
    }
  }
  return changed ? { ...query, rules } : query;
};

export default filterBrokerHiddenFields;
